import React, { useState } from "react";

import { formatDateOnly } from "../../../utils/datetime";
import { formatChf } from "../tenants/tenantOverviewData";
import { TenancyStatusCell } from "../tenants/TenantUnitGroup";
import { DetailTabPanel, DetailTabs } from "../../../components/ui/DetailTabs";
import TenancyCancelDialog from "../tenant/TenancyCancelDialog";
import EntityTasksTab from "../tasks/EntityTasksTab";
import TenancyDocumentsTab from "./TenancyDocumentsTab";
import TenancyFinanceTab, { DepositStatePill } from "./TenancyFinanceTab";
import TenancyNotesTab from "./TenancyNotesTab";
import TenancyTasksPreview from "./TenancyTasksPreview";
import { Card, Field, Pairs, SECONDARY_BUTTON, show } from "./workspaceUi";

/**
 * The tenancy detail, as ONE content tree.
 *
 * A WRAPPER RENDERS THIS, NOT A COPY OF IT. `TenancyDetailPanel` shows it as a
 * right-hand column from `lg` up and `TenancyDetailDrawer` shows the same tree
 * as a sheet below `lg` — the same split `BexioSyncRunsPage` uses. Two
 * implementations would drift the first time a field is added to one of them.
 *
 * EVERY VALUE IS SERVER-DERIVED. The lifecycle ladder lives in
 * `tenancy_lifecycle.tenancy_derived_display_status` and reaches the wire as
 * `display_status`; the canonical move-out reaches it as `display_end_date`.
 * `getTenancyLifecycleMeta` deliberately is NOT used here: it re-derives that
 * ladder client-side against the DEVICE calendar, and a panel that disagreed
 * with the badge in the row behind it would be worse than no panel.
 *
 * The tab order is the product's, not the data's, and is fixed.
 */

const TABS = [
  { id: "overview", label: "Übersicht" },
  { id: "master", label: "Stammdaten" },
  { id: "lifecycle", label: "Laufzeit" },
  // All four fetch, and none should spend a request on a tenancy the reader
  // only glanced at, so `lazyMount` defers their children until the tab is
  // first opened. Every tab in this workspace now shows real data.
  { id: "finance", label: "Finanzen", lazyMount: true },
  { id: "documents", label: "Dokumente", lazyMount: true },
  { id: "tasks", label: "Aufgaben", lazyMount: true },
  { id: "notes", label: "Notizen", lazyMount: true },
];

/**
 * A card action that moves to another tab of THIS panel.
 *
 * Not a link: there is no route for a tenancy tab, and navigating away from
 * the workspace to show something it already contains would lose the list
 * beside it. It flips the panel's own tab state, which is why
 * `TenancyDetailContent` owns that state rather than `DetailTabs`.
 */
function TabLink({ onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={SECONDARY_BUTTON}
    >
      {children}
    </button>
  );
}

/** Primary tenant + everyone else on the contract, from `participants`. */
function peopleOf(tenancy) {
  const all = Array.isArray(tenancy?.participants) ? tenancy.participants : [];
  const nameOf = (p) => {
    const t = p?.tenant || {};
    return (
      t.name ||
      [t.first_name, t.last_name].filter(Boolean).join(" ").trim() ||
      ""
    );
  };
  const primary = all.find((p) => p?.role === "primary_tenant");
  const others = all.filter((p) => p?.role !== "primary_tenant");
  return {
    primaryName: primary ? nameOf(primary) : tenancy?.primary_tenant_display_name,
    primaryEmail: primary ? primary?.tenant?.email : tenancy?.primary_tenant_email,
    others: others.map((p) => ({
      key: p?.tenant_id || nameOf(p),
      name: nameOf(p),
      email: p?.tenant?.email || null,
      role: p?.role || null,
    })),
  };
}

const ROLE_LABELS = {
  co_tenant: "Co-Mieter",
  solidarhafter: "Solidarhafter",
};

export default function TenancyDetailContent({
  tenancy,
  loading,
  error,
  onRetry,
  onClose,
  onCancelled,
}) {
  const [tab, setTab] = useState("overview");
  const [cancelOpen, setCancelOpen] = useState(false);

  if (loading) {
    return (
      <div className="flex flex-col gap-3" aria-busy="true" data-testid="tenancy-detail-loading">
        <span className="sr-only">Mietverhältnis wird geladen …</span>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            aria-hidden="true"
            className="h-[84px] animate-pulse rounded-[10px] border border-border bg-muted/40"
          />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div
        role="alert"
        className="rounded-[10px] border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-[13px] text-rose-700 dark:text-rose-300"
      >
        <p className="m-0">
          <strong className="font-semibold">Fehler beim Laden:</strong> {error}
        </p>
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="mt-2 rounded-[7px] border border-border bg-card px-[10px] py-[4px] text-[12px] text-foreground hover:bg-muted"
          >
            Erneut versuchen
          </button>
        ) : null}
      </div>
    );
  }

  if (!tenancy) return null;

  const people = peopleOf(tenancy);
  // Server-derived: the same gate `cancel_tenancy` re-checks before writing.
  // The frontend never decides on its own whether a tenancy may be cancelled.
  const canCancel = Boolean(tenancy.can_cancel);
  const objectLine = [tenancy.unit_title, tenancy.room_name]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="flex flex-col gap-4">
      {/* ── Header ─────────────────────────────────────────────── */}
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="m-0 truncate text-[17px] font-semibold leading-tight text-foreground">
              {show(tenancy.reference_number)}
            </h2>
            {/* Both facts, same as the list row: a termination that has not
                taken effect yet leaves the contract running. The effective
                date is not repeated here — it is in the Laufzeit tab. */}
            <TenancyStatusCell
              displayStatus={tenancy.display_status}
              isRunning={tenancy.is_running}
              layout="inline"
            />
          </div>
          <p className="m-0 mt-[3px] truncate text-[12.5px] text-muted-foreground">
            {show(objectLine)}
          </p>
        </div>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Detailansicht schliessen"
            className="shrink-0 rounded-[7px] border border-border bg-card px-[9px] py-[4px] text-[13px] leading-none text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            ✕
          </button>
        ) : null}
      </header>

      {/* SEVEN TABS IN A COLUMN. The shared bar is written for a full-width
          page (`px-4 text-[13px]`), which here overflowed so far that three
          tabs sat off-screen behind a scrollbar with no affordance. The
          triggers are tightened for THIS workspace through the list class
          rather than in `DetailTabs`, which the unit and tenant detail pages
          also render. `pb-px` keeps the focus ring of the first tab from
          being clipped by the scroll container. */}
      <DetailTabs
        tabs={TABS}
        value={tab}
        onValueChange={setTab}
        listClassName="pb-px [&>button]:px-[11px] [&>button]:py-2 [&>button]:text-[12.5px]"
      >
        {/* ── Übersicht ────────────────────────────────────────── */}
        <DetailTabPanel tabId="overview">
          <div className="flex flex-col gap-3">
            <Card title="Stammdaten">
              <Pairs>
                <Field label="Mieter" value={people.primaryName} />
                <Field label="E-Mail" value={people.primaryEmail} />
                <Field label="Referenz" value={tenancy.reference_number} />
                <Field label="Einheit" value={tenancy.unit_title} />
                <Field label="Zimmer" value={tenancy.room_name} />
              </Pairs>
              {people.others.length > 0 ? (
                <div className="mt-3 border-t border-border pt-3">
                  <p className="m-0 text-[11px] text-muted-foreground">
                    Weitere Personen
                  </p>
                  <ul className="m-0 mt-1 list-none p-0">
                    {people.others.map((p) => (
                      <li key={p.key} className="text-[13px] text-foreground">
                        {p.name || "—"}
                        {p.role ? (
                          <span className="text-muted-foreground">
                            {" "}
                            · {ROLE_LABELS[p.role] || p.role}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </Card>

            <Card
              title="Laufzeit"
              action={
                canCancel ? (
                  <button
                    type="button"
                    onClick={() => setCancelOpen(true)}
                    className="rounded-[7px] border border-rose-500/40 bg-transparent px-[10px] py-[4px] text-[12px] font-medium text-rose-700 hover:bg-rose-500/10 dark:text-rose-300"
                  >
                    Kündigen
                  </button>
                ) : null
              }
            >
              <Pairs>
                <Field label="Mietbeginn" value={formatDateOnly(tenancy.move_in_date)} />
                <Field
                  label="Mietende"
                  value={formatDateOnly(tenancy.display_end_date)}
                />
                <Field
                  label="Kündigungsfrist"
                  value={
                    tenancy.notice_period_months == null
                      ? null
                      : `${tenancy.notice_period_months} Monate`
                  }
                />
                <Field
                  label="Kündigung erhalten"
                  value={formatDateOnly(tenancy.notice_given_at)}
                />
              </Pairs>
              {/* The server's own reason a cancellation is refused, shown
                  instead of a button that would fail on submit. */}
              {!canCancel && tenancy.cancel_block_reason ? (
                <p className="m-0 mt-3 border-t border-border pt-3 text-[12px] text-muted-foreground">
                  {tenancy.cancel_block_reason}
                </p>
              ) : null}
            </Card>

            {/* The two contract figures the tenancy payload already carries,
                plus the deposit's state from the SAME rule the Finanzen tab
                uses — `DepositStatePill` reads `security_state`, which the
                serializer puts on this wire, so the badge costs no request.
                Everything that needs one (revenue rows, Bexio invoices) stays
                one click away in the tab. */}
            <Card
              title="Finanzen"
              action={
                <TabLink onClick={() => setTab("finance")}>
                  Finanzen öffnen
                </TabLink>
              }
            >
              <Pairs>
                <Field
                  label="Monatsmiete"
                  value={
                    tenancy.monthly_revenue_equivalent == null
                      ? null
                      : formatChf(tenancy.monthly_revenue_equivalent)
                  }
                />
                <div className="min-w-0">
                  <dt className="text-[11px] leading-tight text-muted-foreground">
                    Kaution
                  </dt>
                  <dd className="mt-[2px] flex flex-wrap items-center gap-2 break-words text-[13px] text-foreground">
                    <span>
                      {tenancy.deposit_amount == null
                        ? "—"
                        : formatChf(tenancy.deposit_amount)}
                    </span>
                    <DepositStatePill tenancy={tenancy} />
                  </dd>
                </div>
              </Pairs>
            </Card>

            {/* Documents get a way in, not a preview.
                The tenancy payload carries no document data, and the four
                families behind the tab are equal in rank — so a preview here
                would mean four requests on every row click for a block that
                is one click away, or picking a "most important" document the
                data does not support. Neither is worth it; a count we have
                not loaded would be worse still. */}
            {/* The two entry-only cards sit side by side once there is room.
                They carry a sentence rather than a `Pairs` grid, so halving
                their width costs nothing — and it stops the Übersicht from
                reading as one long single-column tube. Data cards stay full
                width: their two-column field grid would be unreadable at
                half of this column. `xl:` can never apply inside the mobile
                sheet, which only exists below `lg`. */}
            <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            <Card
              title="Dokumente"
              action={
                <TabLink onClick={() => setTab("documents")}>
                  Alle Dokumente öffnen
                </TabLink>
              }
            >
              <p className="m-0 text-[12.5px] text-muted-foreground">
                Mietvertrag, Kündigungsbestätigung, Übergabe- und
                Abnahmeprotokoll, Schlüsselquittung und hochgeladene Dateien
                dieses Mietverhältnisses.
              </p>
            </Card>

            {/* Notes get a way in, not a preview.
                Deliberately no count and no latest-note excerpt: either would
                cost a request on every row click for something one click
                away, and the Notizen tab is lazy precisely so a glanced-at
                tenancy costs nothing. A number we have not loaded would be
                worse still. */}
            <Card
              title="Notizen"
              action={
                <TabLink onClick={() => setTab("notes")}>Zu Notizen</TabLink>
              }
            >
              <p className="m-0 text-[12.5px] text-muted-foreground">
                Wichtige Informationen und Vereinbarungen zu diesem
                Mietverhältnis festhalten.
              </p>
            </Card>
            </div>

            <Card
              title="Verknüpfte Aufgaben"
              action={
                <TabLink onClick={() => setTab("tasks")}>
                  Alle Aufgaben öffnen
                </TabLink>
              }
            >
              <TenancyTasksPreview tenancyId={tenancy.id} />
            </Card>
          </div>
        </DetailTabPanel>

        {/* ── Stammdaten ───────────────────────────────────────── */}
        <DetailTabPanel tabId="master">
          <div className="flex flex-col gap-3">
            <Card title="Mietverhältnis">
              <Pairs>
                <Field label="Referenz" value={tenancy.reference_number} />
                <Field label="Einheit" value={tenancy.unit_title} />
                <Field label="Kurz-ID" value={tenancy.unit_short_unit_id} />
                <Field label="Zimmer" value={tenancy.room_name} />
              </Pairs>
            </Card>

            {/* Only what the tenancy payload already carries. `tenant_summary_dict`
                is id/first_name/last_name/email/name — there is no phone and no
                birth date on this wire, and fetching them per person would be an
                N+1 the list was built to avoid. Those fields are omitted rather
                than rendered blank. */}
            <Card title="Personen">
              <Pairs>
                <Field label="Hauptmieter" value={people.primaryName} />
                <Field label="E-Mail" value={people.primaryEmail} />
              </Pairs>
              {people.others.length > 0 ? (
                <dl className="m-0 mt-3 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-border pt-3">
                  {people.others.map((p) => (
                    <Field
                      key={p.key}
                      label={ROLE_LABELS[p.role] || "Weitere Person"}
                      value={p.email ? `${p.name} · ${p.email}` : p.name}
                      wide
                    />
                  ))}
                </dl>
              ) : null}
            </Card>
          </div>
        </DetailTabPanel>

        {/* ── Laufzeit ─────────────────────────────────────────── */}
        <DetailTabPanel tabId="lifecycle">
          <div className="flex flex-col gap-3">
            <Card title="Vertragsdaten">
              <Pairs>
                <Field label="Mietbeginn" value={formatDateOnly(tenancy.move_in_date)} />
                <Field
                  label="Vertragsende"
                  value={formatDateOnly(tenancy.move_out_date)}
                />
                <Field
                  label="Mindestdauer"
                  value={
                    tenancy.minimum_duration_months == null
                      ? null
                      : `${tenancy.minimum_duration_months} Monate`
                  }
                />
                <Field
                  label="Kündigungsfrist"
                  value={
                    tenancy.notice_period_months == null
                      ? null
                      : `${tenancy.notice_period_months} Monate`
                  }
                />
                {/* The contract rule as a date: give notice as early as the
                    contract allows, and this is when it would take effect.
                    Server-derived — the frontend adds no months and reads no
                    clock, because the answer depends on the Swiss business day
                    and on the minimum duration still running or not.

                    NOT `earliest_termination_date`, which is on the same wire
                    and means the first day notice may be GIVEN. On a two-month
                    notice period the two are two months apart, and showing the
                    earlier one under this label would promise something the
                    contract does not offer.

                    When the contract ends before any new notice could bite,
                    the server says so instead of naming a later date, and this
                    says which end actually comes.

                    Absent (an em dash) once notice has ACTUALLY been given, or
                    the contract has ended or was cancelled: the server sends
                    nothing there. A terminated contract already has a real
                    effective date, shown below under "Kündigung & Auszug", and
                    a hypothetical second one beside it would only compete with
                    it. This branch never invents one — everything it prints
                    comes off the wire. */}
                <Field
                  label="Kündigung frühestens per"
                  value={
                    tenancy.termination_timing_status === "contract_ends_first"
                      ? `Vertrag endet vorher am ${formatDateOnly(
                          tenancy.move_out_date
                        )}`
                      : formatDateOnly(tenancy.earliest_effective_termination_date)
                  }
                  wide
                />
              </Pairs>
            </Card>

            {/* `display_end_date` is the canonical move-out
                (tenancy_lifecycle.tenancy_display_end_date). It is shown beside
                the raw dates rather than recomputed from them: the rule for
                which of the three wins is the backend's, and this panel is not
                a second copy of it. */}
            <Card title="Kündigung & Auszug">
              <Pairs>
                <Field
                  label="Kündigung erhalten"
                  value={formatDateOnly(tenancy.notice_given_at)}
                />
                <Field
                  label="Kündigung wirksam per"
                  value={formatDateOnly(tenancy.termination_effective_date)}
                />
                <Field
                  label="Tatsächlicher Auszug"
                  value={formatDateOnly(tenancy.actual_move_out_date)}
                />
                <Field
                  label="Massgebliches Ende"
                  value={formatDateOnly(tenancy.display_end_date)}
                />
                <Field label="Gekündigt durch" value={tenancy.terminated_by} />
                <Field label="Grund" value={tenancy.cancellation_reason} wide />
              </Pairs>
            </Card>
          </div>
        </DetailTabPanel>

        {/* ── Finanzen ─────────────────────────────────────────── */}
        {/* Vertragswerte (Vantio's Soll) beside the Bexio invoices (the Ist).
            Never a delta between them: an invoice carries a rent month only
            where a draft-ledger row recorded one, so a monthly comparison
            would quietly drop every hand-mapped historical invoice and report
            the hole as unpaid rent. */}
        <DetailTabPanel tabId="finance">
          <TenancyFinanceTab tenancy={tenancy} active={tab === "finance"} />
        </DetailTabPanel>
        <DetailTabPanel tabId="documents">
          <TenancyDocumentsTab
            tenancyId={tenancy.id}
            active={tab === "documents"}
          />
        </DetailTabPanel>
        {/* The shared entity tasks tab, mounted the way every other detail
            surface mounts it — `filter` is the API query shape, and the
            merged create-linkage fix turns it into the form's prefilled link
            set, so "Neue Aufgabe" here produces a task linked to THIS
            tenancy. No tenancy-specific task list exists, deliberately. */}
        <DetailTabPanel tabId="tasks">
          <EntityTasksTab
            bare
            rowLayout="stacked"
            filter={{ tenancy_id: tenancy.id }}
            title="Aufgaben"
          />
        </DetailTabPanel>
        {/* ── Notizen ──────────────────────────────────────────── */}
        {/* Notes on THIS contract, never the person's. A tenant can hold
            several tenancies, so `tenant_notes` could not say which one a
            note belongs to — `tenancy_notes` (migration 169) records that at
            write time, and this tab is the only reader of it here. */}
        <DetailTabPanel tabId="notes">
          <TenancyNotesTab tenancyId={tenancy.id} active={tab === "notes"} />
        </DetailTabPanel>
      </DetailTabs>

      {/* The cancellation flow is the existing dialog, unchanged: it loads its
          own preflight and calls the cancellation service itself. Nothing here
          patches a status. */}
      {cancelOpen ? (
        <TenancyCancelDialog
          tenancy={tenancy}
          onClose={() => setCancelOpen(false)}
          onCancelled={() => {
            setCancelOpen(false);
            if (onCancelled) onCancelled();
          }}
        />
      ) : null}
    </div>
  );
}
